import type { CSSProperties } from 'react';
import type { GeneratedTabPaneProps } from './GeneratedReportTabs';

/**
 * Phase 5e (builder A) — the document/project-scope report tab panes, ported
 * 1:1 from the legacy tab templates. All eleven are generate-only tabs like
 * the 5c family: the Backbone view POSTs the generate request and, on the
 * POST's SUCCESS callback only, renders the SAME static shell (header +
 * "generation in progress" preloader) — the finished file arrives later
 * through the header notifications poll. None render in-tab results, none
 * render synchronously at dispatch (no IMMEDIATE_RENDER_TABS additions), and
 * none are reopenable. Sources (success → render moments):
 *
 *  - the six CMS Documents (Terms & Conditions ×3, Guide Specifications ×3):
 *    one shared view shape — collection.save(FormData{mfg_id:1}).then(render)
 *    (sales_terms.js:58-69, shipping_terms.js:51-62, warranty_terms.js:51-62,
 *    ahu_guide_specs.js:51-62, condensing_unit_guide_specs.js:51-62,
 *    packaged_unit_guide_specs.js:51-62; POST URL from each model/all.js
 *    baseUrl '/reports/cms_documents/<value>/'). Five templates are byte-
 *    identical (shipping_terms.tmpl:1-17 et al.); sales_terms.tmpl differs:
 *    leading whitespace inside .report-name (:3-4 → renders as a leading
 *    space), an empty .time-stamp span (:5), and NO trailing space in the
 *    container-header class (:1). All six put the pane's own id on the inner
 *    structure div (e.g. sales_terms.tmpl:14 `<div id="sales_terms">` INSIDE
 *    the #sales_terms pane — the nested duplicate id is faithful) and an
 *    inline `height: fit-content` on the preloader container (:11), with NO
 *    overflow:auto on the content-item (:10).
 *  - submittal_cover.js:45-57 + tmpl (group "Engineering " with trailing
 *    space, tooltip " : ", #submittal_cover_structure, overflow:auto).
 *  - scope_of_supply.js:45-57 + tmpl (identical shell shape,
 *    #scope_of_supply_structure).
 *  - equipment_list.js:95-110 + equipment_list.tmpl (group "Engineering"
 *    WITHOUT trailing space; the view's template variable is misleadingly
 *    named `reportProjectTmpl` but requires equipment_list.tmpl at :7 —
 *    tmpl/report.project.tmpl is never loaded, dead file).
 *  - equipment_schedule.js:38-52 + tmpl — whose structure div is
 *    id="equipment_list_structure" (:13, copy-paste quirk kept).
 *  - pricing.js:57-69 + pricing.tmpl (group "Sales", tooltip ": ", empty
 *    .time-stamp span, #pricing_structure WITHOUT the row class).
 *
 * Dead legacy machinery deliberately NOT ported (nothing ever reaches it):
 *  - the six documents: `this.data[state] = {model: collection.toJSON()}`
 *    (sales_terms.js:65-67 et al.) is written but never read — the only
 *    open:generated_report listener is commented out (sales_terms.js:27-31)
 *    and the other five never had one. sales_terms's render also does
 *    `$("#export_csv").addClass("non-clickable")` (:39) — main_header.tmpl
 *    renders no #export_csv, a no-op.
 *  - submittal_cover: export_csv (:146-193) — its listener is commented out
 *    (:26); export_pdf/viewPDF fully commented (:93-145); generate socket
 *    block commented (:58-71). The console.log (:42) is debug noise.
 *  - scope_of_supply: export_csv (:78-137) — listener commented (:25);
 *    console.log (:42) noise.
 *  - equipment_list: export_pdf (:44-84) is only called from the commented
 *    generate socket block (:111-123); viewPDF (:126-140) unreferenced; the
 *    six collection files (collection/*.js) are required by NO view in this
 *    tab (only submittal_package requires them) and model/all.js is empty.
 *  - equipment_schedule: export_csv (:67-103) only called from the commented
 *    socket block (:54-65).
 *  - pricing: reportTmplAhu/reportTmplProject are compiled in initialize
 *    (:31-32) but nothing ever renders them (no renderReport path exists;
 *    report.ahu.tmpl's top half is commented-out HTML anyway); export_pdf
 *    (:103-143) hangs off the commented socket block (:70-85) and would
 *    throw on its undefined `state` (:134); viewPDF/validate/setTimeStamp
 *    unreferenced.
 *  - LIVE-but-inert open:generated_report listeners: submittal_cover (:25 →
 *    :75-83), scope_of_supply (:20-24 → :61-69), equipment_list (:21 →
 *    :33-42) and pricing (:30 → :43-50) DO register handlers, but each only
 *    flips data.generated when `this.data[state]` exists — and no live code
 *    ever populates this.data (all socket blocks are commented out), so
 *    `generated` stays false and the empty board re-activates: exactly the
 *    page's default for tabs absent from REOPENABLE_TABS.
 */

/** The shared shell markup shape of all eleven 5e-A templates; the props
 *  carry each template's exact quirks (spaces included). */
interface DocumentShellProps extends GeneratedTabPaneProps {
  /** Exact .group-name text, trailing space included where legacy has one. */
  group: string;
  /** Exact tooltip attribute value. */
  tooltip: string;
  /** container-header class VERBATIM (five documents carry a trailing
   *  space, shipping_terms.tmpl:1 et al.). */
  containerHeaderClass?: string;
  /** sales_terms.tmpl:3-4 — whitespace before {{sectionName}} inside the
   *  .report-name span collapses to a leading space. */
  reportNameLeadingSpace?: boolean;
  /** Empty `.time-stamp` span after .report-name, separated by a space
   *  (sales_terms.tmpl:5 / pricing.tmpl:4); only ever filled by dead code. */
  timeStamp?: boolean;
  /** `style="overflow: auto;"` on the content-item (submittal_cover.tmpl:8
   *  family) — ABSENT on the six documents (sales_terms.tmpl:10). */
  contentItemStyle?: CSSProperties;
  /** `style="height: fit-content;"` on the preloader container — documents
   *  only (sales_terms.tmpl:11). */
  preloaderContainerStyle?: CSSProperties;
  structureId: string;
  structureClass: string;
}

const DocumentTabShell = ({
  report,
  faded,
  rendered,
  group,
  tooltip,
  containerHeaderClass = 'container-header clearfix',
  reportNameLeadingSpace,
  timeStamp,
  contentItemStyle,
  preloaderContainerStyle,
  structureId,
  structureClass,
}: DocumentShellProps) => (
  // Wrapper = the static pane div (main.tmpl, class="col-xs-12") plus the
  // 'active' openReport adds + show() at generate time — same contract as the
  // 5c GeneratedTabShell wrapper.
  <div id={report.value} className="col-xs-12 active" style={{ display: 'block' }}>
    {rendered && (
      <>
        <div className={containerHeaderClass}>
          <h3 className="left">
            {/* sectionName = the .current_report menu button's text, i.e. the
                report display name (e.g. sales_terms.js:40-41). */}
            <span className="group-name" tooltip={tooltip}>
              {group}
            </span>
            <span className="report-name">
              {reportNameLeadingSpace ? ' ' : ''}
              {report.name}
            </span>
            {timeStamp && (
              <>
                {' '}
                <span className="time-stamp" />
              </>
            )}
          </h3>
        </div>
        <div className="report-content col-xs-12">
          <div className="content">
            <div className="content-item full-height" style={contentItemStyle}>
              <div
                className={`preloader-container generation-in-progress${
                  faded ? ' opacity-0' : ''
                }`}
                style={preloaderContainerStyle}
              >
                <div className="preloader active">Report generation in progress...</div>
              </div>
              <div id={structureId} className={structureClass} />
            </div>
          </div>
        </div>
      </>
    )}
  </div>
);

/** sales_terms.tmpl:1-18 — the one document template with the leading
 *  report-name space + empty time-stamp span (and no trailing class space). */
export const SalesTermsTab = (props: GeneratedTabPaneProps) => (
  <DocumentTabShell
    {...props}
    group="Documents"
    tooltip={`:${props.report.name}`}
    reportNameLeadingSpace
    timeStamp
    preloaderContainerStyle={{ height: 'fit-content' }}
    structureId={props.report.value}
    structureClass="row full-height"
  />
);

/** The other five CMS documents — ONE component for the five byte-identical
 *  templates (shipping_terms.tmpl / warranty_terms.tmpl / ahu_guide_specs.tmpl
 *  / condensing_unit_guide_specs.tmpl / packaged_unit_guide_specs.tmpl); the
 *  structure div reuses the pane's own value as its id. */
export const CmsDocumentTab = (props: GeneratedTabPaneProps) => (
  <DocumentTabShell
    {...props}
    group="Documents"
    tooltip={`:${props.report.name}`}
    containerHeaderClass="container-header clearfix "
    preloaderContainerStyle={{ height: 'fit-content' }}
    structureId={props.report.value}
    structureClass="row full-height"
  />
);

/** submittal_cover.tmpl:1-16. */
export const SubmittalCoverTab = (props: GeneratedTabPaneProps) => (
  <DocumentTabShell
    {...props}
    group="Engineering "
    tooltip={` : ${props.report.name}`}
    contentItemStyle={{ overflow: 'auto' }}
    structureId="submittal_cover_structure"
    structureClass="row full-height"
  />
);

/** scope_of_supply.tmpl:1-16. */
export const ScopeOfSupplyTab = (props: GeneratedTabPaneProps) => (
  <DocumentTabShell
    {...props}
    group="Engineering "
    tooltip={` : ${props.report.name}`}
    contentItemStyle={{ overflow: 'auto' }}
    structureId="scope_of_supply_structure"
    structureClass="row full-height"
  />
);

/** equipment_list.tmpl:1-16 — group WITHOUT the trailing space. */
export const EquipmentListTab = (props: GeneratedTabPaneProps) => (
  <DocumentTabShell
    {...props}
    group="Engineering"
    tooltip={` : ${props.report.name}`}
    contentItemStyle={{ overflow: 'auto' }}
    structureId="equipment_list_structure"
    structureClass="row full-height"
  />
);

/** equipment_schedule.tmpl:1-17 — its structure div is ALSO
 *  id="equipment_list_structure" (:13, legacy copy-paste quirk). */
export const EquipmentScheduleTab = (props: GeneratedTabPaneProps) => (
  <DocumentTabShell
    {...props}
    group="Engineering"
    tooltip={` : ${props.report.name}`}
    contentItemStyle={{ overflow: 'auto' }}
    structureId="equipment_list_structure"
    structureClass="row full-height"
  />
);

/** pricing.tmpl:1-17 — Sales group, tooltip ": ", empty time-stamp span,
 *  structure div without the row class. */
export const PricingTab = (props: GeneratedTabPaneProps) => (
  <DocumentTabShell
    {...props}
    group="Sales"
    tooltip={`: ${props.report.name}`}
    timeStamp
    contentItemStyle={{ overflow: 'auto' }}
    structureId="pricing_structure"
    structureClass="full-height"
  />
);
